import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  type ApiQueryError,
  isNetworkApiError,
} from '@/app/common/utils/apiErrorUtils';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useInternetReachable } from '@/hooks/useInternetReachable';

type DataLoadErrorProps = {
  onRetry: () => void;
  error?: ApiQueryError;
  compact?: boolean;
  className?: string;
  isRetrying?: boolean;
  isNetworkError?: boolean;
};

const offlineTitle = 'عدم اتصال به اینترنت :(';
const offlineMessage =
  'لطفا اتصالت به اینترنت رو بررسی کن، اگر وی پی ان داری خاموشش کن و بعدش صفحه رو بارگذاری کن.';

const onlineServerTitle = 'یک خطای غیر منتظره رخ داده است :(';
const onlineServerMessage =
  'لطفا ابتدا از غیرفعال بودن VPN یا افزونه Adblock در مرورگر خود مطمئن شوید. سپس پیشنهاد میشه دوباره از منوی سمت راست، یک دامنه را دوباره انتخاب کنید. در صورتی که همچنان مشکلی وجود دارد، لطفا یک‌بار دیگر وارد حساب کاربری خود شوید یا با پشتیبانی از طریق چت آنلاین در تماس باشید.';

const ErrorContent = ({
  title,
  message,
  compact,
}: {
  title: string;
  message: string;
  compact: boolean;
}) =>
  compact ? (
    <span className="text-700 fs--2">{title}</span>
  ) : (
    <>
      <h5 className="text-1000 mb-2">{title}</h5>
      <p className="text-700 fs--1 mb-3">{message}</p>
    </>
  );

const DataLoadError = ({
  onRetry,
  error,
  compact = false,
  className = '',
  isRetrying = false,
  isNetworkError,
}: DataLoadErrorProps) => {
  const isOnline = useOnlineStatus();
  const hasNetworkError =
    isNetworkError ?? (error ? isNetworkApiError(error) : false);
  const reachable = useInternetReachable(hasNetworkError && isOnline);
  const showOfflineMessage =
    !isOnline || (hasNetworkError && reachable === false);

  const title = showOfflineMessage ? offlineTitle : onlineServerTitle;
  const message = showOfflineMessage ? offlineMessage : onlineServerMessage;
  const statusIcon = showOfflineMessage ? 'wifi' : 'exclamation-circle';
  const statusIconClass = showOfflineMessage ? 'text-danger' : 'text-warning';

  const retryButton = (
    <Button
      size={compact ? 'sm' : undefined}
      variant="outline-secondary"
      className={compact ? 'fs--2' : 'fs--1'}
      onClick={onRetry}
      disabled={isRetrying}
    >
      <FontAwesomeIcon icon="redo" className={compact ? 'me-1' : 'me-2'} />
      تلاش دوباره
    </Button>
  );

  if (compact) {
    return (
      <div
        className={`data-load-error data-load-error--compact d-flex flex-column align-items-center gap-2 text-center ${className}`}
      >
        <FontAwesomeIcon
          icon={statusIcon}
          className={statusIconClass}
          style={{ fontSize: '1.1rem' }}
        />
        <ErrorContent compact title={title} message={message} />
        {retryButton}
      </div>
    );
  }

  return (
    <Card className={`data-load-error text-center ${className}`}>
      <Card.Body className="py-4 px-5 px-md-7 px-xl-8">
        <div className="mb-3">
          <FontAwesomeIcon
            icon={statusIcon}
            className={statusIconClass}
            style={{ fontSize: '2rem' }}
          />
        </div>
        <div className="mx-auto" style={{ maxWidth: '50rem' }}>
          <ErrorContent compact={false} title={title} message={message} />
          {retryButton}
        </div>
      </Card.Body>
    </Card>
  );
};

export default DataLoadError;
